/**
 * SOLID Review
 * S — Strong. One job: detect mutations that are still in flight past a threshold and report start/end via
 *     callbacks. The file's own comment states the design intent — "Deliberately UI-agnostic: it takes
 *     callbacks, so the store/rendering stays out of the network layer" — and the code honors that; it
 *     never touches a store directly.
 * O — Good. thresholdMs and the onSlowStart/onSlowEnd callbacks are injected via IServiceSlowRequestLinkOptions,
 *     so behavior (what counts as slow, what happens when it does) is configurable without editing this
 *     file.
 * L — N/A — no inheritance hierarchy; returns a plain ApolloLink instance via a factory
 *     function, not a subclass.
 * I — Strong. A single factory function with one options interface; nothing forces a caller to depend on
 *     more than thresholdMs/onSlowStart/onSlowEnd.
 * D — Strong. Depends only on @apollo/client primitives (ApolloLink/Observable) — no application or UI
 *     imports. It takes callbacks rather than implementing or depending on an application-layer port,
 *     which is exactly why apolloClient.ts's own call site was able to push the uiStore coupling into
 *     itself instead of here — this file stays clean.
 */
import { ApolloLink, Observable, type FetchResult } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

/**
 * Slow-request detector (§12.36). Load testing showed the backend degrades GRACEFULLY under
 * saturation — it gets slow rather than erroring (p95 604ms normally, but p95 2.5s / p99 3.3s past
 * the knee). Individual buttons already show their own in-flight state ("Announcing…", "Passing…"),
 * which covers the normal case; what was missing is a signal for the ABNORMAL case, where a player
 * would otherwise conclude the game is broken and start refreshing — the worst thing they can do to
 * an already-loaded server.
 *
 * This watches every MUTATION (queries/subscriptions are background traffic and would produce noisy
 * banners) and reports "still in flight after thresholdMs" so the UI can show a transient hint.
 * Counts concurrent slow operations so the hint clears only when the LAST one finishes.
 *
 * Deliberately UI-agnostic: it takes callbacks, so the store/rendering stays out of the network layer.
 */
export interface IServiceSlowRequestLinkOptions {
  /** How long a mutation may run before it is considered slow. */
  thresholdMs?: number;
  /** Called when the first slow mutation crosses the threshold. */
  onSlowStart: () => void;
  /** Called when the last slow mutation finishes. */
  onSlowEnd: () => void;
}

export function createSlowRequestLink({
  thresholdMs = 1500,
  onSlowStart,
  onSlowEnd,
}: IServiceSlowRequestLinkOptions): ApolloLink {
  let slowCount = 0;

  const markSlow = (): void => {
    slowCount += 1;
    if (slowCount === 1) onSlowStart();
  };
  const clearSlow = (wasSlow: boolean): void => {
    if (!wasSlow) return;
    slowCount = Math.max(0, slowCount - 1);
    if (slowCount === 0) onSlowEnd();
  };

  return new ApolloLink((operation, forward) => {
    const def = getMainDefinition(operation.query);
    const isMutation = def.kind === 'OperationDefinition' && def.operation === 'mutation';
    if (!isMutation) return forward(operation);

    return new Observable<FetchResult>((observer) => {
      let wasSlow = false;
      let settled = false;
      const timer = setTimeout(() => { wasSlow = true; markSlow(); }, thresholdMs);

      // Idempotent: unsubscribe can fire after complete/error, and double-counting would strand
      // the hint on screen forever.
      const settle = (): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        clearSlow(wasSlow);
      };

      const sub = forward(operation).subscribe({
        next: (value) => observer.next(value),
        error: (err) => { settle(); observer.error(err); },
        complete: () => { settle(); observer.complete(); },
      });

      return () => { settle(); sub.unsubscribe(); };
    });
  });
}
